import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findApiError } from '@/utils/requestHandling';
import type { ApiErrorItem } from '@/utils/requestHandling';
import { getApiErrorMessage } from '@/utils/apiErrors';
import {
  CLAWBACK_CONSENT_REQUIRED,
  CLAWBACK_CONSENT_STALE,
  CLAWBACK_REQUIRES_HR,
} from '@/models/tournament';
import type { ClawbackPreview } from '@/models/tournament';
import type { ClawbackConsentDialogProps } from './ClawbackConsentDialog';

/**
 * CO-921/CO-922 Round 2: HR must knowingly approve every claw-back of pay already given to officials.
 *
 * The server refuses such a change with 409 and a preview (`errors[0].details`); approving means sending
 * the SAME request again with the preview's token in `X-Clawback-Consent`. `utils/requestHandling`
 * rejects every non-2xx with the parsed body, so the refusal arrives as a thrown rejection: `run` catches
 * it with `findApiError` (which keeps `details`), opens the dialog, and on Confirm calls `send(token)`.
 * A stale token (amounts moved since the preview) comes back as `clawbackConsentStale` with a fresh
 * preview, and the dialog simply re-shows it with the stale notice. Any other rejection from the FIRST
 * send is rethrown untouched, so the caller's existing catch keeps working for ordinary errors.
 *
 * `ask` is the lower-level entry for the bulk import, whose preview arrives with the analyze response
 * instead of a refusal. After a 'confirm' or 'skip' the dialog stays open and locked until the caller
 * calls `close()` (or `ask` again, e.g. with a fresh preview).
 */

export type ClawbackChoice = 'confirm' | 'skip' | 'cancel';

export type ClawbackRunOutcome<T> =
  | { status: 'done'; result: T }
  /** HR kept everything as it is; nothing was sent with consent, nothing changed. */
  | { status: 'declined' }
  /** The caller is not HR (Admin, an official): the server refused and nothing changed. */
  | { status: 'requiresHr'; message: string };

interface AskOptions {
  stale?: boolean;
  bulk?: boolean;
  error?: string | null;
}

interface DialogState {
  preview: ClawbackPreview;
  stale: boolean;
  bulk: boolean;
  busy: boolean;
  error: string | null;
}

const CONSENT_CODES = [CLAWBACK_CONSENT_REQUIRED, CLAWBACK_CONSENT_STALE, CLAWBACK_REQUIRES_HR];

/** The consent refusal carried by a rejection OR a resolved Result, or null when it is something else. */
export function findClawbackRefusal(source: unknown): ApiErrorItem | null {
  return findApiError(source, ...CONSENT_CODES);
}

function previewOf(refusal: ApiErrorItem | null): ClawbackPreview | null {
  const d = refusal?.details as ClawbackPreview | null | undefined;
  return d && typeof d === 'object' && Array.isArray(d.officials) ? d : null;
}

type Attempt<T> = { result: T } | { refusal: ApiErrorItem } | { error: unknown };

export function useClawbackConsent() {
  const { t } = useTranslation('tournament');
  const [state, setState] = useState<DialogState | null>(null);
  const resolverRef = useRef<((choice: ClawbackChoice) => void) | null>(null);

  const ask = useCallback(
    (preview: ClawbackPreview, opts: AskOptions = {}) =>
      new Promise<ClawbackChoice>((resolve) => {
        resolverRef.current = resolve;
        setState({ preview, stale: !!opts.stale, bulk: !!opts.bulk, busy: false, error: opts.error ?? null });
      }),
    [],
  );

  const choose = useCallback((choice: ClawbackChoice) => {
    const resolve = resolverRef.current;
    if (!resolve) return; // already answered: a double click or a late Esc while in flight
    resolverRef.current = null;
    if (choice === 'cancel') setState(null);
    else setState((s) => (s ? { ...s, busy: true, error: null } : s));
    resolve(choice);
  }, []);

  const close = useCallback(() => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    resolve?.('cancel');
  }, []);

  const requiresHrMessage = t('Only HR can take back pay. Nothing was changed.');

  const run = useCallback(
    async <T>(send: (consent?: string) => Promise<T>): Promise<ClawbackRunOutcome<T>> => {
      const attempt = async (consent?: string): Promise<Attempt<T>> => {
        try {
          const result = await send(consent);
          const refusal = findClawbackRefusal(result);
          return refusal ? { refusal } : { result };
        } catch (error) {
          const refusal = findClawbackRefusal(error);
          return refusal ? { refusal } : { error };
        }
      };

      const first = await attempt();
      if ('result' in first) return { status: 'done', result: first.result };
      if ('error' in first) throw first.error;

      let refusal = first.refusal;
      let preview = previewOf(refusal);
      let stale = refusal.code === CLAWBACK_CONSENT_STALE;
      let error: string | null = null;

      for (;;) {
        if (refusal.code === CLAWBACK_REQUIRES_HR || !preview?.consentToken) {
          setState(null);
          return { status: 'requiresHr', message: requiresHrMessage };
        }

        const choice = await ask(preview, { stale, error });
        if (choice !== 'confirm') {
          setState(null);
          return { status: 'declined' };
        }

        const next = await attempt(preview.consentToken);
        if ('result' in next) {
          setState(null);
          return { status: 'done', result: next.result };
        }
        if ('refusal' in next) {
          refusal = next.refusal;
          preview = previewOf(next.refusal) ?? preview;
          stale = next.refusal.code === CLAWBACK_CONSENT_STALE;
          error = null;
          continue;
        }
        // Any other failure on the approved send: keep the dialog and say what happened, so HR can retry
        // or keep everything as it is. The server rolls the whole change back on failure.
        stale = false;
        error = getApiErrorMessage(next.error, t, t('The change could not be saved. Nothing was changed.'), ' ');
      }
    },
    [ask, t, requiresHrMessage],
  );

  const dialogProps: ClawbackConsentDialogProps = {
    open: state != null,
    preview: state?.preview ?? null,
    stale: state?.stale ?? false,
    bulk: state?.bulk ?? false,
    busy: state?.busy ?? false,
    error: state?.error ?? null,
    onConfirm: () => choose('confirm'),
    onSkip: () => choose('skip'),
    onCancel: () => choose('cancel'),
  };

  return { run, ask, close, dialogProps, requiresHrMessage };
}
